import React, { useEffect, useState, Fragment, useRef, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import TagManager from 'react-gtm-module';
import { FormattedMessage } from 'react-intl';
import { push as pushToHistory } from 'redux-first-history';
import { ThemeProvider } from 'styled-components';

// Components
import ConnectedIntlProvider from '../utils/ConnectedIntlProvider';
import FooterContainer from '../containers/common/FooterContainer';
import HeaderContainer from '../containers/common/HeaderContainer';
import HyperDashboard from './hyperDashboard/HyperDashboard';
import { DebugLabelSelector } from './hyperDashboard/DebugI18nSelector';
import ModalContainer from '../containers/common/ModalContainer';
import SpinnerContainer from '../containers/common/SpinnerContainer';
import SystemErrorMessage from './ssap/notifications/SystemErrorMessage';
import Feedback from './common/feedback/Feedback';
import { CookieConsent } from '@modules/cookieConsent';
// ActionCreators
import { getEnvironment, fetchMockModal, fetchUnreadMessages, startSpinner, stopSpinner } from '../actions/common/actionCreators';
import { fetchUserPermissions } from '../actions/userPermissions/userPermissionsActionCreators';

// Hooks

import { useLocalStorage } from '../hooks/useLocalStorage';
import { useLocation } from '../hooks/useLocation';
import { useMockModal } from '../hooks/useMockModal';
import { useNavigation } from '../hooks/useNavigation';

// Constants

import { IEX_STATE_CODES } from '@constants/configs/states';
import { ALL_USER_ROLES } from '../constants/roles/userRoleTypes';
import { configs } from '../constants/configs/configs';
import { SPINNER_MESSAGES_IDS } from '@constants/common/miscellaneous';

// Keycloak Service

import KeycloakService from '../services/keycloakService';

// Font awesome react setup

import '../constants/configs/FontAwesomeConfig';

// GTM setup

import GTagManager from '../services/gtmConfig';

// routes

import MemberPortalRoutes from '../routes/MemberPortalRoutes';
import { GARoutes } from '../routes/GARoutes';

// Modules

import { GeorgiaHeader } from '../modules/georgia-access';

//hooks
import useSessionIdle from '../hooks/useSessionIdle';
import useNiceCXOneChat from '../hooks/useNiceCXOneChat';

const App = () => {
    const dispatch = useDispatch();
    const { environment: environmentFromStore, adjustedFlow, data, page, permissionsLoaded } = useSelector(
        state => ({
            environment: state.common.environment,
            adjustedFlow: state.ssap.adjustedFlow,
            data: state.ssap.data,
            page: state.ssap.page,
            permissionsLoaded: state.common.userPermissions?.loaded || state.common.userPermissions?.error,
        }),
        shallowEqual,
    );

    const [isStyleLoaded, setStyleLoaded] = useState(!global.__ENV__.PRODUCTION);
    const [environment, setEnvironment] = useState(null);
    const { ASSISTERENROLLMENTENTITY, ASSISTER } = ALL_USER_ROLES;
    const roleSpecificSurveyMonkeyLink = [ASSISTERENROLLMENTENTITY, ASSISTER];
    const [theme, setTheme] = useState({});

    const {
        endpoints,
        externalEndpoints: { GENERAL, ID_LINKS },
    } = configs;

    const { navigateToLocation } = useNavigation(destination => dispatch(pushToHistory(destination)));
    useNiceCXOneChat(environment);

    const { initSessionIdle, idleTimer, sessionConfig } = useSessionIdle();
    const { currentLocation } = useLocation();

    useLocalStorage();
    useMockModal({
        adjustedFlow,
        page,
        data,
        fetchMockModal: (...args) => dispatch(fetchMockModal(...args)),
    });


    useEffect(() => {
        dispatch(getEnvironment());
        navigateToLocation();
    }, []);

    const isKeycloakEnabled = useMemo(() => {
        return !global.__ENV__?.DEVELOPMENT && !!environment?.ssoConfig;
    }, [environment?.ssoConfig]);

    useEffect(() => {
        const isUserAuthenticated = environment?.anonymous === false;
        if (isUserAuthenticated && isKeycloakEnabled) {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            KeycloakService.initSilent().finally(() => {
                dispatch(stopSpinner());
            });
        }
    }, [environment?.anonymous, isKeycloakEnabled]);


    useEffect(() => {
        async function loadThemeData(profileKey, isMNFullStack) {
            try {
                const buildVersion = __VITE_BUILD_VERSION__;
                const themePath = `${window.location.origin}/hix/resources/spa/build/assets/css/index_${profileKey}${isMNFullStack ? '_fs' : ''}.module.css?v=${buildVersion}`;
                // Dynamically import the CSS as a JS object (assuming CSS Modules setup)
                const themeModule = configs.ENV.PRODUCTION
                    ? await fetch(themePath)
                        .then(async data => {
                            const cssText = await data.text();
                            const exportBlock = Array.from(cssText.matchAll(/:export\s*{([^}]*)}/gs)).map(match => {
                                return match[1].trim();
                            });
                            if (exportBlock) {
                                const variables = exportBlock.reduce((prevVars, block) => {
                                    const blockVars = block
                                        .split(';')
                                        .filter(Boolean)
                                        .reduce((vars, line) => {
                                            const [key, value] = line.split(':').map(s => s.trim());
                                            vars[key] = value;
                                            return vars;
                                        }, {});
                                    return { ...prevVars, ...blockVars };
                                }, {});
                                return variables;
                            }
                        })
                        .catch(error => {
                            console.log('err', error);
                        })
                    : (await import(`../../build/assets/css/index_${profileKey}${isMNFullStack?'_fs':''}.module.css`)).default;

                setTheme(themeModule || {});
                // `themeModule` will have the exported CSS properties
            } catch (error) {
                console.error(
                    'Failed to load theme data:',
                    error,
                    `path: ${window.location.origin}/hix/resources/spa/build/assets/css/index_${profileKey}${isMNFullStack?'_fs':''}.module.css`
                );
            }
        }
        let interval = null;
        function loadStyles(profileKey, isMNFullStack) {
            if (profileKey && configs.ENV.PRODUCTION) {
                const isFireFox = typeof InstallTrigger !== 'undefined';
                const buildVersion = __VITE_BUILD_VERSION__;
                if (isFireFox) {
                    const style = document.createElement('style');
                    if (configs.ENV.CUSTOM) {
                        style.textContent = `@import "/build/css/index_${profileKey}${isMNFullStack?'_fs':''}.css"`;
                    } else {
                        style.textContent = `@import "/hix/resources/spa/build/assets/css/index_${profileKey}${isMNFullStack ? '_fs' : ''}.module.css?v=${buildVersion}"`;
                    }

                    const fi = setInterval(() => {
                        try {
                            style.sheet.cssRules;
                            setStyleLoaded(true);
                            clearInterval(fi);
                        } catch (err) {
                            console.log('In App: error in loading css, err = ', err);
                        }
                    }, 500);

                    document.querySelector('head').appendChild(style);
                } else {
                    const link = document.createElement('link');
                    link.setAttribute('rel', 'stylesheet');
                    if (configs.ENV.CUSTOM) {
                        link.setAttribute('href', `/build/css/index_${profileKey}${isMNFullStack?'_fs':''}.css`);
                    } else {
                        link.setAttribute('href', `/hix/resources/spa/build/assets/css/index_${profileKey}${isMNFullStack?'_fs':''}.module.css?v=${buildVersion}`);
                    }
                    link.addEventListener('load', () => {
                        setTimeout(() => {
                            setStyleLoaded(true);
                        }, 500);
                    });
                    document.querySelector('head').appendChild(link);

                    const ti = setInterval(() => {
                        try {
                            if (link.sheet && link.sheet.cssRules.length > 0) {
                                clearInterval(ti);
                            } else if (link.styleSheet && link.styleSheet.cssText.length > 0) {
                                clearInterval(ti);
                            } else if (link.innerHTML && link.innerHTML.length > 0) {
                                clearInterval(ti);
                            }
                        } catch (err) {
                            console.log('In App: error in loading css, err = ', err);
                            setStyleLoaded(true);
                            clearInterval(ti);
                        }
                    });
                }
            } else {
                if (profileKey) {
                    const link = document.createElement('link');
                    link.setAttribute('rel', 'stylesheet');
                    link.setAttribute('href', `/build/assets/css/index_${profileKey}${isMNFullStack?'_fs':''}.module.css`);
                    document.querySelector('head').appendChild(link);
                }
            }
        }

        function initTagManager(gaContainerId) {
            if (gaContainerId !== null && gaContainerId) {
                TagManager.initialize({ gtmId: gaContainerId });
            }
        }

        function initializeYHIChat(stateCode) {
            if (stateCode === IEX_STATE_CODES.ID && environment && !environment.devEnvironment) {
                const script = document.createElement('script');
                script.src = 'https://sharpenchat.iz1.sharpen.cx';
                script.setAttribute(
                    'appKey',
                    'D189D445901F97206794420A773B73245BE20279398C0BD736B78C2035CB8F0DAE0A18441D128F9B',
                );
                script.async = true;
                document.body.appendChild(script);
            }
        }


        if (environmentFromStore) {
            const {
                anonymous,
                clientInactivityThreshold,
                links,
                popupThresholdValue,
                properties,
                profile,
                stateCode,
                isMNFullStack,
            } = environmentFromStore;

            const profileKey = profile ?? stateCode;
            const prevProfileKey = environment?.profile ?? environment?.stateCode;

            const logoutUrl = links?.LOGOUT;                    
            const GOOGLE_ANALYTICS_CONTAINER_ID = properties?.GOOGLE_ANALYTICS_CONTAINER_ID;

            initSessionIdle(anonymous, clientInactivityThreshold, popupThresholdValue, logoutUrl);
            initializeYHIChat(stateCode);
            setEnvironment(environmentFromStore);
            if (prevProfileKey !== profileKey) {
                loadStyles(profileKey, isMNFullStack);
                loadThemeData(profileKey, isMNFullStack);
            }
        }

        return () => clearInterval(interval);
    }, [environmentFromStore]);

    const ranOnceRef = useRef(false);

    useEffect(() => {
        if (environment && environment.anonymous === false && !ranOnceRef.current) {
            ranOnceRef.current = true;
            dispatch(fetchUnreadMessages(endpoints.UNREAD_MESSAGE_COUNT, {}));
            dispatch(fetchUserPermissions());
        }
    }, [environment]);

    if (
        environment && !environment.anonymous && window.location.pathname === environment?.links?.LOGIN && environment?.links?.DASHBOARD
    ) {
        window.location.href = environment?.links?.DASHBOARD;
    }



    function openSurveyMonkey(stateCode) {
        const hasSurveyMonkeyLink = roleSpecificSurveyMonkeyLink.includes(environment.user.role.toUpperCase());
        const stateCodeMap = stateCode === IEX_STATE_CODES.ID ? ID_LINKS : GENERAL;
        const link = hasSurveyMonkeyLink && stateCode === IEX_STATE_CODES.ID ? stateCodeMap.ROLE_BASED_LINK : stateCodeMap.DEFAULT_SURVEY_LINK;
        window.open(link, '_blank');
    }

    function renderGAPortal() {
        return (
            <Fragment>
                <GeorgiaHeader {...{ environment: environment }} />
                <SystemErrorMessage>
                    <GARoutes />
                </SystemErrorMessage>
                <FooterContainer />
            </Fragment>
        );
    }

    function renderMemberPortal() {
        return (
            <Fragment>
                <HeaderContainer />
                <SystemErrorMessage>
                    <MemberPortalRoutes
                        {...{
                            isState: environment.isState,
                            stateCode: environment.stateCode,
                            ticketEnabled: environment.ticketEnabled,
                            userData: environment.user,
                            leadPresent: environment.screenerConfigs.leadPresent,
                            isMNFullStack: environment.isMNFullStack,
                            isStateFullStack: environment.isStateFullStack,
                            showHistoricalData: environment.flags.showHistoricalData,
                            historicalMigratedUser: environment.flags.historicalMigratedUser,
                        }}
                    />
                </SystemErrorMessage>
                <FooterContainer />
            </Fragment>
        );
    }

    function renderMediaAccess() {
        return (
            <Fragment>
                <SystemErrorMessage>
                    <MemberPortalRoutes
                        {...{
                            stateCode: environment.stateCode,
                            ticketEnabled: environment.ticketEnabled,
                            userData: environment.user,
                            leadPresent: environment.screenerConfigs.leadPresent,
                        }}
                    />
                </SystemErrorMessage>
            </Fragment>
        );
    }

    if (!environment) {
        return null;
    }

    if (!isStyleLoaded || (!permissionsLoaded && !environment.anonymous)) {
        return (
            <ConnectedIntlProvider>
                <div>
                    <FormattedMessage id="common.loading" />
                </div>
            </ConnectedIntlProvider>
        );
    }

    const isGeorgiaLite =
        environment &&
        environment.isState.GA &&
        ((environment.user && environment.user.role === ALL_USER_ROLES.INDIVIDUAL_LITE) || environment.anonymous);

    const isMediaAccess = window.location && window.location.pathname === configs.routes.MEDIA_ACCESS && window.location.search.includes('contactId');

    function renderPortal() {
        if (isGeorgiaLite) return renderGAPortal();
        if (isMediaAccess) return renderMediaAccess();
        return renderMemberPortal();
    }

    return (
        <ConnectedIntlProvider>
            <ThemeProvider theme={theme}>
                {/* CookieConsent must wrap GTagManager so consent mode is bootstrapped before GTM initializes. */}
                <CookieConsent isEnabled={environment?.flags?.isConsentBannerEnabled}>
                    <ModalContainer />
                    <SpinnerContainer />
                    <Feedback
                        ALL_USER_ROLES={ALL_USER_ROLES}
                        environment={environment}
                        openSurveyMonkey={openSurveyMonkey}
                    />
                    <DebugLabelSelector {...{ idleTimer, sessionConfig }} />
                    <HyperDashboard {...{ currentLocation }} />
                    <GTagManager
                        user={environment.user}
                        GOOGLE_ANALYTICS_CONTAINER_ID={environment.properties.GOOGLE_ANALYTICS_CONTAINER_ID}
                    />
                    {renderPortal()}
                </CookieConsent>
            </ThemeProvider>
        </ConnectedIntlProvider>
    );
};

export default App;
